import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { CheckCard } from "../../components/CheckCard";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { ROUTE } from "../../routes";
import { ApplicationTypeNewFrame } from "./ApplicationTypeNewFrame";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useChecks } from "../../data/useChecks";
import styled from "styled-components";
import { notify } from "../../utils/notify";
import { useUpdateMandateType } from "../../data/useUpdateMandateType";
import { useBillingInfo } from "../../data/useBillingInfo";
import { Paragraph } from "../../components/Paragraph";
import { useMandateType } from "../../data/useMandateType";

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(334px, 1fr));
  ${({ theme: { space } }) => `
  grid-gap: ${space[3]};
  overflow: hidden;
  margin-top: 26px;
  `}
`;

const SettingsApplicationTypeNewChecks = () => {
  const [checkpoints, setCheckpoints] = useState<number[]>([]);
  const navigate = useNavigate();
  const params = useParams();

  const { mutate: updateMandateType } = useUpdateMandateType();
  const { data: billingData } = useBillingInfo();
  const [selectedMandateTypeId, setSelectedMandateTypeId] = useState<
    number | null
  >(null);

  const billingInfo =
    billingData?.data?.results?.length > 0
      ? billingData?.data?.results[0]
      : null;

  useEffect(() => {
    const id = parseInt(params.id as string, 10);
    setSelectedMandateTypeId(Number.isNaN(id) ? null : id);
  }, [params.id]);

  const { data: mandateTypeQuery, isLoading: isLoadingMandateType } =
    useMandateType(selectedMandateTypeId);

  const selectedType = useMemo(() => {
    const t =
      (mandateTypeQuery as any)?.data?._type ??
      (mandateTypeQuery as any)?._type ??
      undefined;
    return t as "individual" | "entity" | undefined;
  }, [mandateTypeQuery]);

  const { data: checkListQuery, isLoading: isLoadingChecks } =
    useChecks(selectedType);
  const checkList = checkListQuery?.data;

  const allChecks = useMemo(
    () => (checkList ?? []).sort((a: any, b: any) => a.order - b.order),
    [checkList]
  );

  const visibleChecks = useMemo(() => {
    if (!allChecks) return [];

    let hiddenChecks: string[] = [];

    if (selectedType === "individual") {
      hiddenChecks = [
        "personal_details_match_check",
        "additional_document_check",
      ];
    }

    if (selectedType === "entity") {
      hiddenChecks = [
        "entity_details_match_check",
        "entity_additional_document_check",
      ];
    }

    return allChecks.filter((c: any) => !hiddenChecks.includes(c.name));
  }, [allChecks, selectedType]);

  const handleCheckpoints = (id: number, isAdding: boolean) => {
    setCheckpoints((prev) =>
      isAdding ? [...prev, id] : prev.filter((cp) => cp !== id)
    );
  };

  const handleSubmit = () => {
    if (!selectedMandateTypeId) return;

    const personalDetailsId = allChecks?.find(
      (c: any) => c.name === "personal_details_match_check"
    )?.id;

    const entityDetailsId = allChecks?.find(
      (c: any) => c.name === "entity_details_match_check"
    )?.id;

    let base = checkpoints;

    if (selectedType === "individual") {
      base = [...base, ...(personalDetailsId ? [personalDetailsId] : [])];
    }

    if (selectedType === "entity" && entityDetailsId) {
      base = [...base, entityDetailsId];
    }

    const finalCheckpoints = Array.from(new Set(base));

    updateMandateType(
      {
        params: { id: selectedMandateTypeId, checkpoint: finalCheckpoints },
        id: selectedMandateTypeId,
      } as any,
      {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2")) {
            notify("Checks added successfully!", { type: "success" });
          }
          navigate(
            `/settings/application-types/${selectedMandateTypeId}/forms`
          );
        },
      }
    );
  };

  const isLoading = isLoadingMandateType || isLoadingChecks;

  return (
    <ApplicationTypeNewFrame step="checks">
      <Box pl={4} pr={2} pt={4} flex={4} minWidth={400}>
        <H2 fontSize="3">Create New Application Type</H2>

        <Box pt={2}>
          <H2 fontSize={3} fontWeight={500}>
            Checks
          </H2>

          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" minHeight="25vh">
              <Loader />
            </Flex>
          ) : !visibleChecks || visibleChecks.length === 0 ? (
            <Flex justifyContent="center" alignItems="center" minHeight="25vh">
              <Paragraph>No checks available.</Paragraph>
            </Flex>
          ) : (
            <Grid>
              {visibleChecks.map((check: any) => {
                const isDisabled =
                  billingInfo?.billing_type === "Basic" &&
                  !check.billing_type?.includes?.("Basic");

                return (
                  <CheckCard
                    key={check.id}
                    id={check.id}
                    name={check.name}
                    actual_name={check.actual_name}
                    check_type={check.check_type}
                    handleCheckpoints={handleCheckpoints}
                    is_disabled={!!isDisabled}
                  />
                );
              })}
            </Grid>
          )}
        </Box>

        <Flex mt={4} justifyContent="end">
          <Button
            mr="2"
            size="regular"
            variant="secondary"
            onClick={() => navigate(ROUTE.SETTINGS_APPLICATION_TYPES)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            size="regular"
            isDisabled={!checkpoints || checkpoints.length === 0}
          >
            Create
          </Button>
        </Flex>
      </Box>
    </ApplicationTypeNewFrame>
  );
};

export default SettingsApplicationTypeNewChecks;
